import { Editor, EditorRef } from '@/components/editor/Editor';
import { Viewer } from '@/components/editor/Viewer';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import React, { useRef, useState, useEffect } from 'react';
import { FieldComponentProps } from './types';

// FIXME: some buttons cause the form to submit

export const RichTextField: React.FC<FieldComponentProps> = ({
  formMode,
  controllerField: { onChange, value, ...controllerFieldRest },
  field,
}) => {
  const editorRef = useRef<EditorRef>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);

  // Check if content needs truncation based on actual rendered height
  useEffect(() => {
    if (contentRef.current && formMode === 'view') {
      // Small delay to ensure content is rendered
      const timer = setTimeout(() => {
        if (contentRef.current) {
          const height = contentRef.current.scrollHeight;
          // Truncate if content is taller than ~6 lines of text (approx 144px)
          setShouldTruncate(height > 144);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [value, formMode]);

  if (formMode === 'loading') {
    return <Skeleton className="h-24 w-full" />;
  }
  if (formMode === 'edit') {
    return (
      <div
        className={cn(
          `focus-within:ring-ring/50 w-full overflow-hidden rounded-lg border
          transition-colors focus-within:ring-[3px]`,
        )}
      >
        <Editor
          ref={editorRef}
          defaultValue={value || ''}
          onChange={onChange}
          namespace="rich-text-field"
          placeholder={field.meta.placeholder}
          onBlur={controllerFieldRest.onBlur}
          disabled={controllerFieldRest.disabled}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Container with measured content */}
      <div className="relative">
        <div
          ref={contentRef}
          className={cn(
            'overflow-hidden transition-[max-height] duration-300 ease-in-out',
            !isExpanded && shouldTruncate && 'max-h-36', // 144px = ~6 lines of text
          )}
        >
          <Viewer className="border-none px-0 py-0 shadow-none" value={value} />
        </div>

        {/* Gradient fade overlay - only visible when collapsed and truncated */}
        {!isExpanded && shouldTruncate && (
          <div
            className="from-form-surface via-form-surface/60 pointer-events-none absolute inset-x-0
              bottom-0 h-16 bg-gradient-to-t to-transparent"
            aria-hidden="true"
          />
        )}
      </div>

      {/* Show more/less button */}
      {shouldTruncate && (
        <div className="relative mt-2 flex justify-center">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              'group inline-flex items-center gap-1.5 px-2 py-1',
              'text-muted-foreground text-sm font-medium',
              'hover:text-foreground hover:bg-muted/50',
              'rounded-md transition-all duration-200',
              `focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1
              focus-visible:outline-none`,
            )}
            aria-expanded={isExpanded}
            aria-label={
              isExpanded ? 'Collapse description' : 'Expand full description'
            }
          >
            {isExpanded ? 'Show less' : 'Show all'}
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform duration-200',
                isExpanded && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </button>
        </div>
      )}
    </div>
  );
};
